import { useEffect } from 'react'
import { hasRememberedUndo, lastTouchedUndoRegion, runUndo } from '@/src/renderer/lib/undo'
import { showInfoToast } from '@/src/renderer/components/common/MessageToast'
import { APP_UNDO_EVENT } from './useUndoRouter'

/**
 * `⌘Z` / `Strg+Z` in den Aufgabenlisten — es nimmt **die gemerkte Handlung**
 * zurück (`lib/undo`, ADR 0028). Das Versprechen: deine letzte Änderung geht
 * zurück; kann sie das nicht, passiert nichts.
 *
 * **Zwei Wege hinein**, wie beim Canvas: der Tastendruck (Web) und das
 * Ereignis aus `useUndoRouter` (Electron — dort verbraucht das native Menü die
 * Taste, bevor der Renderer sie sieht).
 *
 * **Kein `⇧⌘Z`.** Es gibt nur eine Stufe; ein Wiederherstellen gäbe es nicht
 * zurückzuholen.
 *
 * **Im Textfeld gehört `⌘Z` der Eingabe.** Wer tippt, will seinen letzten
 * Anschlag zurück, nicht die gelöschte Aufgabe von vorhin.
 *
 * **Die Pinnwand bekommt die Taste, wenn sie zuletzt berührt wurde** — nicht,
 * solange sie offen ist. Wer links in der Liste etwas löscht und `⌘Z` drückt,
 * meint die Liste, auch wenn rechts das Brett steht.
 *
 * **Ein Hinweis sagt, was jetzt gilt** („Blumen gießen ist wieder da.") — aber
 * nur, wenn wirklich etwas geschehen ist. War nichts gemerkt, schweigt die
 * Taste: Das ist der Normalfall, sobald man etwas Nicht-Rücknehmbares getan
 * hat, und eine Meldung käme dann ständig.
 *
 * Muss app-weit montiert sein — die Handlung kann von überall kommen.
 *
 * @example
 * useLastUndoShortcut()   // einmal, in der App-Wurzel
 */
export function useLastUndoShortcut() {
  useEffect(() => {
    const take = async () => {
      const message = await runUndo()
      if (message) showInfoToast(message)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      // ⌥⌘Z und ⇧⌘Z gehören uns nicht.
      if (e.altKey || e.shiftKey) return
      // `code` statt `key`: Auf einem deutschen Layout läge `key` sonst auf „y".
      if (e.code !== 'KeyZ') return
      if (isTyping(e.target)) return
      if (lastTouchedUndoRegion() !== 'list') return

      /*
        Erst fragen, dann aufhalten: Steht nichts an, läuft die Taste
        unangetastet weiter zu dem, dem sie sonst gehört. `preventDefault`
        muss dabei synchron fallen — nach dem ersten `await` ist es zu spät.
      */
      if (!hasRememberedUndo()) return
      e.preventDefault()
      void take()
    }

    const handleUndoEvent = () => {
      // Textfelder hat `useUndoRouter` schon abgezweigt.
      if (lastTouchedUndoRegion() !== 'list') return
      void take()
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener(APP_UNDO_EVENT, handleUndoEvent)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener(APP_UNDO_EVENT, handleUndoEvent)
    }
  }, [])
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  const tag = el?.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || !!el?.isContentEditable
}
